import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { LayoutGrid, Pencil } from 'lucide-react';
import EditorStolu from './EditorStolu';
import { SECTION_COLOR, CERVENA, nacti, posli, tlumene, cara, Nadpis, Tlacitko } from '../sprava/ui';

/**
 * Správa → Provozovna → Stoly (2026-10-20): stejný editor půdorysu jako
 * v pokladně, jen online z OS. Volá tytéž endpointy (`/api/pos/nastaveni/…`,
 * jen admin) — pokladny si změnu načtou samy.
 *
 * Editor potřebuje celou plochu, proto se otevírá přes celou obrazovku.
 * Jeho barvy jsou proměnné pokladny; tady je nastaví obal, ať se nepletou
 * se zbytkem OS.
 */
const PROMENNE = {
    tmave: {
        '--tint-rgb': '255,255,255', '--text-inverse': '#0A0A0C', '--busy': '#F59E0B', '--danger': '#EF4444',
        '--info': '#3B82F6', '--success': '#10B981', '--tyrkys': '#14B8A6', '--kanal-bistro': '#10B981',
        '--sklo-bg': 'linear-gradient(150deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
        '--sklo-okraj': 'rgba(255,255,255,0.10)', '--sklo-odlesk': 'inset 0 1px 0 rgba(255,255,255,0.08)',
        '--sklo-rozostreni': 'blur(32px) saturate(130%)',
    },
    svetle: {
        '--tint-rgb': '0,0,0', '--text-inverse': '#FFFFFF', '--busy': '#92400E', '--danger': '#B91C1C',
        '--info': '#1D4ED8', '--success': '#047857', '--tyrkys': '#0F766E', '--kanal-bistro': '#047857',
        '--sklo-bg': 'linear-gradient(150deg, #FFFFFF 0%, #F1EEE7 100%)',
        '--sklo-okraj': 'rgba(0,0,0,0.16)', '--sklo-odlesk': 'inset 0 1px 0 rgba(255,255,255,0.9)',
        '--sklo-rozostreni': 'none',
    },
};

const api = {
    ulozitRozlozeni: (stoly) => posli('/api/pos/nastaveni/stoly/rozlozeni', 'PUT', { stoly }),
    ulozitStul: (id, data) => (id ? posli(`/api/pos/nastaveni/stoly/${id}`, 'PATCH', data) : posli('/api/pos/nastaveni/stoly', 'POST', data)),
    deaktivovatStul: (id) => posli(`/api/pos/nastaveni/stoly/${id}`, 'DELETE'),
    ulozitZonu: (id, data) => (id ? posli(`/api/pos/nastaveni/zony/${id}`, 'PATCH', data) : posli('/api/pos/nastaveni/zony', 'POST', data)),
    deaktivovatZonu: (id) => posli(`/api/pos/nastaveni/zony/${id}`, 'DELETE'),
};

export default function EditorStoluSprava({ upravuje, isDark, isMobile }) {
    const ml = tlumene(isDark);
    const [data, setData] = useState(null);
    const [chyba, setChyba] = useState(null);
    const [zona, setZona] = useState(null);

    const obnov = useCallback(() => nacti('/api/pos/stoly')
        .then((d) => { setData(d); setChyba(null); })
        .catch((e) => setChyba(e.message)), []);
    useEffect(() => { obnov(); }, [obnov]);

    const zony = data?.zony ?? [];
    const stoly = data?.stoly ?? [];
    const pocet = (z) => stoly.filter((s) => s.zona_id === z.id && !s.dekorace).length;
    const mist = (z) => stoly.filter((s) => s.zona_id === z.id && !s.dekorace).reduce((a, s) => a + (Number(s.kapacita) || 0), 0);
    // Editor bere aktuální podobu sekce ze seznamu (po uložení se změní).
    const aktualniZona = zona ? zony.find((z) => z.id === zona.id) ?? zona : null;

    return (
        <>
            {chyba && <p className="text-sm" style={{ color: CERVENA }}>{chyba}</p>}
            {!data && !chyba && <p className="text-sm" style={{ color: ml }}>Načítám…</p>}
            {data && (
                <>
                    <p className="text-xs" style={{ color: ml, margin: '0 0 12px' }}>
                        Půdorys provozovny — stejný editor jako v pokladně. Změny se pokladnám projeví po jejich obnovení.
                    </p>
                    <Nadpis isDark={isDark}>Sekce</Nadpis>
                    {zony.length === 0 && <p className="text-sm" style={{ color: ml }}>Zatím žádná sekce.</p>}
                    {zony.map((z) => (
                        <div key={z.id} className="flex items-center gap-3" style={{ padding: '10px 0', borderBottom: `1px solid ${cara(isDark)}` }}>
                            <LayoutGrid size={16} style={{ color: SECTION_COLOR, flexShrink: 0 }} />
                            <div className="flex-1 min-w-0">
                                <p className="truncate font-semibold" style={{ margin: 0, fontSize: 13.5, color: 'var(--text-primary)' }}>{z.nazev}</p>
                                <p style={{ margin: '2px 0 0', fontSize: 11.5, color: ml }}>{pocet(z)} stolů · {mist(z)} míst</p>
                            </div>
                            {upravuje && !isMobile && <Tlacitko isDark={isDark} Icon={Pencil} onClick={() => setZona(z)}>Upravit</Tlacitko>}
                        </div>
                    ))}
                    {upravuje && !isMobile && zony.length === 0 && (
                        <div style={{ marginTop: 12 }}>
                            <Tlacitko isDark={isDark} plna Icon={Pencil} onClick={() => setZona({ id: null, nazev: 'Nová sekce' })}>Otevřít editor</Tlacitko>
                        </div>
                    )}
                    {isMobile && <p className="text-xs" style={{ color: ml, marginTop: 12 }}>Editor stolů je na počítači nebo tabletu.</p>}
                </>
            )}

            {aktualniZona && createPortal(
                <div className="editor-stolu fixed inset-0 flex flex-col" style={{
                    zIndex: 1000, background: 'var(--surface-base, var(--bg-primary))', color: 'var(--text-primary)',
                    ...PROMENNE[isDark ? 'tmave' : 'svetle'],
                }}>
                    <EditorStolu
                        zona={aktualniZona}
                        zony={zony}
                        onZonu={setZona}
                        stoly={stoly}
                        api={api}
                        onZmena={obnov}
                        onZavrit={() => { setZona(null); obnov(); }}
                    />
                </div>,
                document.body,
            )}
        </>
    );
}
